'use strict';

(() => {
  // Sets up a global object to hold custom functions and classes.
  window.mx = window.mx || {};

  /**
   * Sets the CSS variable --scrollbar-width to the width of the scrollbar.
   */
  const setScrollBarWidthProperty = () => {
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollBarWidth}px`);
  };
  window.addEventListener('resize', setScrollBarWidthProperty);
  setScrollBarWidthProperty();

  /**
   * Returns whether the provided element is currently locked.
   * @param {HTMLElement} [element=document.documentElement] - The element to check.
   * @returns {boolean} - Whether the element is locked.
   */
  window.mx.isScrollLocked = (element = document.documentElement) => {
    return element.hasAttribute('data-scroll-lock');
  };

  /**
   * Locks scrolling on the provided element and saves the current scroll position.
   * @param {HTMLElement} [element=document.documentElement] - The element to lock scrolling on.
   */
  window.mx.lockScroll = (element = document.documentElement) => {
    element.style.scrollBehavior = 'auto';
    element.style.top = `-${window.scrollY}px`;
    element.setAttribute('data-scroll-lock', '');
  };

  /**
   * Unlocks scrolling on the provided element and restores the previous scroll position.
   * @param {HTMLElement} [element=document.documentElement] - The element to unlock scrolling on.
   */
  window.mx.unlockScroll = (element = document.documentElement) => {
    element.removeAttribute('data-scroll-lock');
    window.scrollTo(0, -1 * parseInt(element.style.top));
    element.style.top = null;
    element.style.scrollBehavior = null;
  };

  /**
   * Manages event listeners for easy addition and removal.
   */
  window.mx.EventManager = class {
    constructor() {
      this._listeners = new Map();
    }

    /**
     * Adds an event listener and manages it for easy removal.
     * @param {EventTarget} element - The element to attach the event listener to.
     * @param {String} type - The type of event to listen for.
     * @param {Function} handler - The function to be called when the event occurs.
     * @param {Object} options - Options for the event listener.
     */
    addListener(element, type, handler, options) {
      if (!(element instanceof EventTarget)) {
        throw new Error('Invalid element provided to addListener');
      }

      element.addEventListener(type, handler, options);

      if (!this._listeners.has(element)) {
        this._listeners.set(element, new Set());
      }
      this._listeners.get(element).add({ type, handler });
    }

    /**
     * Removes a listener for the event.
     */
    removeListener(element, type, handler) {
      element.removeEventListener(type, handler);
    }

    /**
     * Removes all managed event listeners.
     */
    removeAllListeners() {
      for (const [element, listeners] of this._listeners) {
        listeners.forEach(({ type, handler }) => {
          this.removeListener(element, type, handler);
        });
      }
      this._listeners.clear();
    }
  };

  /**
   * Creates a throttled function that only invokes the provided callback function at most once per animation frame.
   * @param {Function} callback - The callback function to be throttled.
   * @returns {Function} - The throttled function.
   */
  window.mx.throttle = (callback) => {
    let requestId = null;
    let lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  };

  /**
   * Creates a debounced function that delays invoking the provided callback function.
   * @param {Function} callback - The callback function to be debounced.
   * @param {number} [delay=100] - The delay in milliseconds before invoking the callback function.
   * @returns {Function} - The debounced function.
   */
  window.mx.debounce = (callback, delay = 100) => {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callback.apply(this, args);
      }, delay);
    };
  };

  window.mx.formatPrice = (rawPrice) => {
    const integer = Math.floor(rawPrice / 100);
    const decimal = rawPrice - Math.floor(rawPrice / 100) * 100;

    return decimal === 0 ? `${integer},00€` : `${integer},${decimal}€`;
  };

  window.mx.MxComponent = class extends HTMLElement {
    constructor() {
      super();
      this.eventManager = new window.mx.EventManager();
    }

    connectedCallback() {
      // Use this.eventManager.addListener to add easy cleanup event listeners.
    }

    disconnectedCallback() {
      this.eventManager.removeAllListeners();
    }
  };
  if (!window.customElements.get('mx-component')) {
    window.customElements.define('mx-component', window.mx.MxComponent);
  }

  window.mx.MxSection = class extends window.mx.MxComponent {
    constructor() {
      super();
    }

    get section() {
      return this.closest('.shopify-section');
    }
  };
  if (!window.customElements.get('mx-section')) {
    window.customElements.define('mx-section', window.mx.MxSection);
  }

  window.mx.MxDrawer = class extends window.mx.MxComponent {
    constructor() {
      super();
    }

    get overlay() {
      return this.section.querySelector('.mx-overlay');
    }

    open() {
      this.section.setAttribute('data-open', '');
      window.mx.lockScroll();
    }
    close() {
      this.section.removeAttribute('data-open');
      window.mx.unlockScroll();
    }
  };
  if (!window.customElements.get('mx-drawer')) {
    window.customElements.define('mx-drawer', window.mx.MxDrawer);
  }
})();

document.addEventListener('DOMContentLoaded', () => {
  const announcementBar = document.querySelector('.announcement-bar-section');

  function setAnnouncementBarHeightProperty() {
    document.documentElement.style.setProperty('--announcement-bar-height', `${announcementBar?.offsetHeight || 0}px`);
  }

  if (announcementBar) {
    new ResizeObserver(setAnnouncementBarHeightProperty).observe(announcementBar);
    setAnnouncementBarHeightProperty();
  }

  const searchIcon = document.querySelector('.thb-quick-search');

  searchIcon.addEventListener('click', () => {
    const searchField = document.querySelector('#side-panel-search-input');
    searchField.focus();
  });

  window.mx.ComplementaryProductOptions = class extends window.mx.MxComponent {
    constructor() {
      super();

      this.cachedConfig = null;
    }

    connectedCallback() {
      this.optionValues.forEach((optionValue) => {
        this.eventManager.addListener(optionValue, 'click', () => this.onOptionValueClick(optionValue));
      });

      this.eventManager.addListener(document, 'product:variant-change', (event) => {
        if (event.detail.variant) {
          this.config.base_price = event.detail.variant.price;
          this.config.base_compare_at_price = event.detail.variant.compare_at_price;
        }
      });
    }

    get config() {
      if (this.cachedConfig === null) {
        const configElement = this.querySelector(`script`);

        try {
          this.cachedConfig = configElement ? JSON.parse(configElement.textContent) : {};
        } catch (error) {
          console.error('Error parsing slider JSON configuration', this, error);
        }
      }

      return this.cachedConfig;
    }
    get optionValues() {
      return this.querySelectorAll('.option-value-container');
    }
    get productInformation() {
      return this.closest('.product-information');
    }
    get productWrapper() {
      return this.closest('.thb-product-detail');
    }
    get productSlider() {
      return this.productWrapper.querySelector('product-slider');
    }
    get sectionId() {
      return this.productWrapper.closest('.product-section').id.replace('shopify-section-', '');
    }
    get productPrice() {
      return this.productInformation.querySelector('.product-price-container .price ins .amount');
    }
    get productComparePrice() {
      return this.productInformation.querySelector('.product-price-container .price del .amount');
    }
    get stickyProductPrice() {
      return document.querySelector(`#price-${this.sectionId}--sticky .price ins .amount`);
    }
    get buttonPrice() {
      return document.getElementById(`button-price-${this.sectionId}`);
    }
    get stickyButtonPrice() {
      return document.getElementById(`button-price-${this.sectionId}--sticky`);
    }

    onOptionValueClick(optionValue) {
      const optionList = optionValue.closest('ul');

      if (optionValue.hasAttribute('data-active')) {
        optionValue.removeAttribute('data-active');
      } else {
        Array.from(optionList.children).forEach((child) => {
          child.removeAttribute('data-active');
        });
        optionValue.setAttribute('data-active', '');
      }

      this.updateProductPrice();

      if (optionValue.hasAttribute('data-active')) {
        const variantId = optionValue.getAttribute('data-variant-id');
        const mediaId = `#Slide-${this.sectionId}-complementary-${variantId}`;
        this.productSlider.selectCell(mediaId);
      }
    }

    updateProductPrice() {
      let optionsPrice = 0;
      let optionsComparePrice = 0;

      for (const optionValue of this.optionValues) {
        if (optionValue.hasAttribute('data-active')) {
          const variantId = optionValue.getAttribute('data-variant-id');
          optionsPrice += this.config.option_prices[variantId];
          optionsComparePrice += this.config.option_compare_at_prices?.[variantId] ?? this.config.option_prices[variantId];
        }
      }

      const formattedPrice = window.mx.formatPrice(this.config.base_price + optionsPrice);
      this.productPrice.textContent = formattedPrice;
      if (this.stickyProductPrice) {
        this.stickyProductPrice.textContent = formattedPrice;
      }
      if (this.buttonPrice) {
        this.buttonPrice.innerHTML = `<span class="separator">—</span> ${formattedPrice}`;
      }
      if (this.stickyButtonPrice) {
        this.stickyButtonPrice.innerHTML = `<span class="separator">—</span> ${formattedPrice}`;
      }

      // Keep the compare-at price in sync with the bundle (only rendered when the product is discounted)
      if (this.productComparePrice && this.config.base_compare_at_price != null) {
        this.productComparePrice.textContent = window.mx.formatPrice(this.config.base_compare_at_price + optionsComparePrice);
      }
    }
  };
  if (!window.customElements.get('complementary-product-options')) {
    window.customElements.define('complementary-product-options', window.mx.ComplementaryProductOptions);
  }
});
